import { useQuery } from '@apollo/client'
import { useState } from 'react'

import { gql } from '@/clients/utoniq-core'
import {
  ArtistThreadChannelOrderByType,
  ConsoleEventAggregationConnectionFragmentDoc,
  ConsoleHomeQuery,
  ConsoleHomeQueryVariables,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { DateTime } from '@/core/entities'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const [updatedAt] = useState(DateTime.now())

  const { data, loading } = useQuery<
    ConsoleHomeQuery,
    ConsoleHomeQueryVariables
  >(QUERY, {
    variables: {
      inputs: {
        artistId: user?.uid ?? '',
        options: {
          orderBy: ArtistThreadChannelOrderByType.CreatedAt,
        },
      },
      limit: 1,
    },
  })

  return {
    state: {
      loading,
      artistId: user?.uid ?? '',
      totalSales: data?.salesReport.formattedTotalSales ?? '0',
      memberships: data?.artist?.formattedPatronCount ?? '0',
      videoId: '',
      campaign: null,
      currentUser: user,
      artist: data?.artist ?? null,
      tips: {
        itemCleared: !!data?.artistItems.edges.length,
        postCleared: !!data?.artist?.posts.edges.length,
        threadCleared: !!data?.artistThreadChannels?.edges.length,
      },
      events: data?.artistEvents ?? null,
      updatedAt: loading ? '' : updatedAt,
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {},
  }
})

const QUERY = gql`
  query ConsoleHome($inputs: ArtistThreadChannelsInput!, $limit: Int) {
    salesReport {
      totalSales
      formattedTotalSales @client
    }
    artist {
      id
      paymentType
      subscription {
        products {
          productId
        }
      }
      status
      patronCount
      formattedPatronCount @client
      followerCount
      totalPurchasedNumber
      hasArrangingDeliveryItems
      subscription {
        products {
          plans {
            amount
            status
          }
          content
        }
      }
      posts(pageInput: { limit: $limit }) {
        edges {
          node {
            id
          }
        }
      }
    }
    artistItems(pageInput: { limit: $limit }) {
      edges {
        node {
          id
        }
      }
    }
    artistEvents(filter: all) {
      ...ConsoleEventAggregationConnection
    }
    artistThreadChannels(inputs: $inputs, pageInput: { limit: $limit }) {
      edges {
        node {
          id
        }
      }
    }
  }
  ${ConsoleEventAggregationConnectionFragmentDoc}
`
